import React, {useEffect, useState, useMemo, useRef} from 'react';
import {
    Box,
    MenuItem,
    TextField,
    Select,
    FormControl,
    InputLabel,
    Typography,
    CircularProgress
} from '@mui/material';
import PropTypes from 'prop-types';
import RichEditorResultText from './Shared/RichEditorResultText';
import RichEditor from './Shared/RichEditor';
import RequiredCheckbox from './Shared/RequiredCheckbox';
import {useGetContactTitlesQuery, useGetRelationsQuery} from '../../../../../api/services/lookupsService';
import {useGetBoarderGroupsQuery} from '../../../../../api/services/groupService';
import {stripHtmlTags} from './AdditionalNotes';

const lookupOptions = [
    {value: 'title', label: 'Title'},
    {value: 'gender', label: 'Gender'},
    {value: 'relationship', label: 'Relationship'},
    {value: 'boarderStatus', label: 'Boarder Status'},
];

const genders = [
    {title: 'Male', value: 'male'},
    {title: 'Female', value: 'female'}
];

const useLookupData = (lookupType) => {
    const {
        data: titles = [],
        isLoading: isLoadingTitles
    } = useGetContactTitlesQuery(undefined, {skip: lookupType !== 'title'});
    const {
        data: relationships = [],
        isLoading: isLoadingRelationships
    } = useGetRelationsQuery(undefined, {skip: lookupType !== 'relationship'});
    const {
        data: boarderStatuses = [],
        isLoading: isLoadingBoarderStatuses
    } = useGetBoarderGroupsQuery(undefined, {skip: lookupType !== 'boarderStatus'});

    const data = useMemo(() => {
        switch (lookupType) {
            case 'title':
                return titles.map(item => ({id: item.id, description: item.description}));
            case 'gender':
                return genders.map(item => ({id: item.value, description: item.title}));
            case 'relationship':
                return relationships.map(item => ({id: item.code, description: item.description}));
            case 'boarderStatus':
                return boarderStatuses.map(item => ({id: item.id, description: item.name}));
            default:
                return [];
        }
    }, [lookupType, titles, relationships, boarderStatuses]);

    const isLoading = isLoadingTitles || isLoadingRelationships || isLoadingBoarderStatuses;

    return {data, isLoading};
};

const LookupDropdown = ({
                            index,
                            id,
                            fieldKey,
                            value,
                            onChange,
                            description,
                            lookupType,
                            isEditMode,
                            enableValidation,
                            isFormArchived,
                            accessLevel,
                            readonly,
                            placeholderValues,
                            error,
                            audience,
                            recipientType,
                            submitMode
                        }) => {

    const {data: fetchedOptions, isLoading} = useLookupData(lookupType);
    const [currentValue, setCurrentValue] = useState(description ?? '');
    const prevLookupTypeRef = useRef(lookupType);

    useEffect(() => {
        setCurrentValue(description ?? '');
    }, [description]);

    const handleDescriptionChange = (nextValue) => {
        setCurrentValue(nextValue);

        if (!onChange) return;

        onChange({
            index,
            description: stripHtmlTags(nextValue),
            formattedDescription: nextValue,
        });
    };

    useEffect(() => {
        if (prevLookupTypeRef.current !== lookupType && !isEditMode && !isLoading) {
            if (value && !fetchedOptions.some(option => option.id === value)) {
                onChange({id, radioValue: ''});
            }
            prevLookupTypeRef.current = lookupType;
        }
    }, [lookupType, isEditMode, isLoading, fetchedOptions, value, id, onChange]);

    useEffect(() => {
        if (isEditMode || prevLookupTypeRef.current !== lookupType) {
            prevLookupTypeRef.current = lookupType;
        }
    }, [lookupType, isEditMode]);

    const handleLookupTypeChange = (event) => {
        onChange({index, lookupType: event.target.value});
    };

    const renderEditMode = () => (
        <Box sx={{mt: 2}}>
            <RichEditor
                value={description}
                onChange={onChange}
                id={id}
                index={index}
                isEditMode={isEditMode}
                isFormLive={isFormArchived}
                accessLevel={accessLevel}
                currentDescription={currentValue}
                setCurrentDescription={handleDescriptionChange}
                audience={audience}
                recipientType={recipientType}
            />
            <FormControl fullWidth margin="normal">
                <InputLabel id={`lookup-type-select-label-${index}`}>Lookup Type</InputLabel>
                <Select
                    labelId={`lookup-type-select-label-${index}`}
                    value={lookupType}
                    label="Lookup Type"
                    onChange={handleLookupTypeChange}
                    disabled={isFormArchived || fieldKey}
                >
                    {lookupOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Typography variant="caption" display="block" gutterBottom>
                Options will be loaded based on the selected Lookup Type.
            </Typography>
            <RequiredCheckbox
                enableValidation={enableValidation}
                index={index}
                onChange={onChange}
                isFormArchived={isFormArchived || fieldKey}
            />
        </Box>
    );

    const renderViewMode = () => (
        <Box>
            <RichEditorResultText
                id={id}
                description={description}
                placeholderValues={placeholderValues}
                readonly={readonly}
                enableValidation={enableValidation}
                error={!!error}
                audience={audience}
                recipientType={recipientType}
                submitMode={submitMode}
            />
            <Box display="flex">
                {isLoading
                    ? <CircularProgress size={24}/>
                    : (
                        <TextField
                            select
                            value={value?.id || ''}
                            fullWidth
                            error={!!error}
                            helperText={error}
                            onChange={e => {
                                if (!readonly) {
                                    const selectedOption = fetchedOptions.find(option => option.id === e.target.value);
                                    if (!selectedOption) return;

                                    onChange({
                                        id,
                                        radioValue: {
                                            id: selectedOption.id,
                                            description: selectedOption.description
                                        }
                                    });
                                }
                            }}
                            disabled={readonly || isLoading}
                            SelectProps={{
                                MenuProps: {
                                    autoFocus: false,
                                    disableAutoFocusItem: true,
                                    disableEnforceFocus: true,
                                    disableAutoFocus: true,
                                }
                            }}
                        >
                            <MenuItem value="" disabled>
                                <em>Select an option</em>
                            </MenuItem>
                            {
                                readonly && value
                                    ? (
                                        <MenuItem value={value.id}>
                                            {value.description}
                                        </MenuItem>
                                    )
                                    : fetchedOptions?.map(option => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.description}
                                        </MenuItem>
                                    ))
                            }
                        </TextField>
                    )
                }
            </Box>
        </Box>
    );

    return (
        <Box sx={{mt: 2}}>
            {isEditMode ? renderEditMode() : renderViewMode()}
        </Box>
    );
};

LookupDropdown.propTypes = {
    id: PropTypes.string,
    value: PropTypes.string,
    index: PropTypes.number,
    fieldKey: PropTypes.string,
    lookupType: PropTypes.oneOf(lookupOptions.map(o => o.value)),
    description: PropTypes.string,
    enableValidation: PropTypes.bool,
    onChange: PropTypes.func,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readonly: PropTypes.bool,
    accessLevel: PropTypes.number,
    placeholderValues: PropTypes.object,
    onUpdateDescription: PropTypes.func,
    componentToEditIndex: PropTypes.number,
    error: PropTypes.string,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    submitMode: PropTypes.bool
};

export default LookupDropdown;
